import CircularTimerButton, {
  CircularTimerButtonProps,
} from "@/components/TimerButton/CircularTimerButton/CircularTimerButton";
import { VenueReservation } from "@/graphql/operations";
import useReservation, {
  getReservationStatus,
  isPendingReservationUpdate,
  reservationHasPassed,
  reservationStatus,
} from "@/hooks/useReservation";
import useUser from "@/hooks/useUser";
import { parseDateString } from "@/tools/date/conversion";
import icons, { Icon } from "@/tools/icons";
import { Box, BoxProps, useTheme } from "@mui/material";
import { OverridableComponent } from "@mui/material/OverridableComponent";
import { SvgIconOwnProps, SvgIconTypeMap } from "@mui/material/SvgIcon";
import { resetReservationGraceTimeMilliseconds } from "@shared/constants";
import { useMemo } from "react";

type StatusIconProps = Pick<CircularTimerButtonProps, "sx"> & {
  reservation: VenueReservation;
};
export default function StatusIcon({ reservation, sx }: StatusIconProps) {
  const { resetReservation } = useReservation();
  const { isMtUser } = useUser();
  const theme = useTheme();
  const disabled = useMemo(
    () =>
      reservationHasPassed(reservation) ||
      (!!reservation.answer &&
        reservation.answeredBy === "MagicalTrip" &&
        !isMtUser),
    [reservation, isMtUser],
  );

  const {
    color,
    Icon,
    onClick,
  }: {
    color: SvgIconOwnProps["color"];
    onClick?: (reservationId: VenueReservation["id"]) => void;
    Icon: Icon;
  } = useMemo(
    () =>
      ({
        [reservationStatus.accepted]: {
          Icon: icons.Accept,
          color: "success" as SvgIconOwnProps["color"],
          onClick: resetReservation,
        },
        [reservationStatus.rejected]: {
          Icon: icons.Reject,
          color: "error" as SvgIconOwnProps["color"],
          onClick: resetReservation,
        },
        [reservationStatus.cancelled]: {
          Icon: icons.Cancel,
          color: "disabled" as SvgIconOwnProps["color"],
        },
        [reservationStatus.pending]: {
          Icon: icons.Unknown,
          color: (disabled ? "error" : "secondary") as SvgIconOwnProps["color"],
        },
      })[getReservationStatus(reservation)],
    [reservation, disabled, resetReservation],
  );

  const outlineColor = useMemo(() => {
    switch (color) {
      case "success":
        return theme.palette.success.main;
      case "error":
        return theme.palette.error.main;
      case "secondary":
        return theme.palette.secondary.main;
      case "disabled":
        return theme.palette.action.disabled;
      default:
        return theme.palette.text.primary;
    }
  }, [color, theme]);

  return (
    <Box position="relative">
      <CircularTimerButton
        sx={sx}
        color={color}
        disabled={disabled}
        pause={!reservation.answer}
        onClick={(event) => {
          event.stopPropagation();
          onClick?.(reservation.id);
        }}
        size={41}
        thickness={6}
        start={
          reservation.answer
            ? reservation.reservationAnswerReceived
              ? parseDateString(reservation.reservationAnswerReceived).getTime()
              : undefined
            : undefined
        }
        duration={resetReservationGraceTimeMilliseconds / 2}
        disableTimer={disabled}
      >
        <Icon
          sx={{
            outline: `2px solid ${outlineColor}`,
            borderRadius: "50%",
            outlineOffset: "-4px",
          }}
          fontSize="large"
          color={color}
          data-testid="status-icon"
        />
      </CircularTimerButton>
      <SimpleBadge
        visible={isPendingReservationUpdate(reservation)}
        sx={{ bottom: 2, right: -12 }}
        Icon={icons.Change}
      />

      <SimpleBadge
        visible={
          !!(reservation.additionalInfo || reservation.cancellation?.reason)
        }
        sx={{ top: 2, right: -12 }}
        Icon={icons.Info}
      />
    </Box>
  );
}

type SimpleBadgeProps = BoxProps & {
  visible?: boolean;
  Icon: OverridableComponent<SvgIconTypeMap<unknown, "svg">> & {
    muiName: string;
  };
};
function SimpleBadge({ visible, Icon, ...boxProps }: SimpleBadgeProps) {
  const { sx, ...otherBoxProps } = boxProps;
  return (
    <Box
      sx={{
        position: "absolute",
        borderRadius: "50%",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        lineHeight: 1,
        ...sx,
      }}
      {...otherBoxProps}
    >
      {visible && <Icon />}
    </Box>
  );
}
